import { useEffect, useState, useCallback } from "react";
import api, { formatErr } from "../../../lib/api";
import { card, btn, btnGhost, label, inputStyle } from "../roads/_roadsCommon";

/** Ch 28 — Real Estate Site & Landscape (Wave L). */
export default function SitePlanPanel({ projectId }) {
  const [model, setModel] = useState(null);
  const [palette, setPalette] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [activeCat, setActiveCat] = useState("boundary");
  const [err, setErr] = useState("");

  const reload = useCallback(async () => {
    try {
      const [m, p, c] = await Promise.all([
        api.get(`/projects/${projectId}/site-re/model`),
        api.get(`/projects/${projectId}/site-re/palette`),
        api.get(`/projects/${projectId}/site-re/compliance`),
      ]);
      setModel(m.data.model);
      setPalette(p.data);
      setCompliance(c.data);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  }, [projectId]);
  useEffect(() => { reload(); }, [reload]);

  const addElement = async (defn) => {
    // Generate sample geometry
    let geom_type = defn.geom; let points = [];
    const n = (model?.elements?.length || 0);
    if (geom_type === "point") points = [[10 + n * 4, 10]];
    else if (geom_type === "rect") points = [[10 + n * 4, 10], [10 + n * 4 + (defn.default_w_m || 4), 10 + (defn.default_h_m || 4)]];
    else if (geom_type === "polyline") points = [[0, 10 + n * 3], [20, 10 + n * 3]];
    else if (geom_type === "polygon") points = [[0, 0], [10, 0], [10, 10], [0, 10]];
    try {
      await api.post(`/projects/${projectId}/site-re/elements`,
        { type: defn.id, geom_type, points });
      reload();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };
  const delElement = async (eid) => {
    await api.delete(`/projects/${projectId}/site-re/elements/${eid}`);
    reload();
  };

  const updatePlan = async (patch) => {
    const newPlan = { ...model.plan, ...patch };
    await api.put(`/projects/${projectId}/site-re/plan`, newPlan);
    reload();
  };

  const cats = ["boundary", "vehicular", "pedestrian", "landscape",
                  "amenity", "utility", "building"];
  const filteredPalette = (palette?.elements || []).filter(e => e.category === activeCat);

  return (
    <div data-testid="site-plan" style={{ display: "grid",
            gridTemplateColumns: "260px 1fr 280px", gap: 12, padding: 16,
            height: "calc(100vh - 280px)", minHeight: 700 }}>
      {/* Palette */}
      <div style={{ ...card, padding: 12, overflowY: "auto" }}>
        <div style={label}>Palette</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
          {cats.map(c => (
            <button key={c} data-testid={`site-cat-${c}`}
                style={{ ...btnGhost, padding: "4px 8px", fontSize: 9,
                            ...(activeCat === c ? { background: "#F26B1F", color: "#0e0e10" } : {}) }}
                onClick={() => setActiveCat(c)}>{c}</button>
          ))}
        </div>
        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 4 }}>
          {filteredPalette.map(e => (
            <button key={e.id} data-testid={`site-add-${e.id}`}
                style={{ ...btnGhost, textAlign: "left", padding: "6px 8px", fontSize: 11 }}
                onClick={() => addElement(e)}>+ {e.label}</button>
          ))}
        </div>
      </div>

      {/* Elements list */}
      <div style={{ display: "grid", gridTemplateRows: "1fr", gap: 12, minHeight: 0 }}>
        <div style={{ ...card, padding: 12, overflowY: "auto" }}>
          <div style={label}>Site Elements ({model?.elements?.length || 0})</div>
          <table style={{ width: "100%", marginTop: 8, fontSize: 11 }}>
            <thead><tr style={{ borderBottom: "1px solid var(--pf-border)" }}>
              {["Label", "Category", "Geom", "Points", ""].map(h =>
                <th key={h} style={{ textAlign: "left", padding: 4, color: "var(--pf-muted)" }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {(model?.elements || []).map(e => (
                <tr key={e.id} data-testid={`site-element-${e.id}`}>
                  <td style={{ padding: 4 }}>{e.label}</td>
                  <td style={{ padding: 4 }}>{e.category}</td>
                  <td style={{ padding: 4 }}>{e.geom_type}</td>
                  <td style={{ padding: 4, fontSize: 10 }}>{e.points?.length || 0} pts</td>
                  <td><button style={{ ...btnGhost, padding: "2px 6px", fontSize: 10 }}
                    onClick={() => delElement(e.id)}>×</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Plan + compliance */}
      <div style={{ display: "grid", gridTemplateRows: "auto 1fr", gap: 12, minHeight: 0 }}>
        <div style={{ ...card, padding: 12 }}>
          <div style={label}>Plot Configuration</div>
          {model?.plan && (
            <div style={{ marginTop: 8, display: "grid", gap: 6, fontSize: 11 }}>
              <Row label="Plot area m²"
                    value={model.plan.plot_area_m2}
                    onSave={(v) => updatePlan({ plot_area_m2: parseFloat(v) || 0 })} />
              <Row label="DCR FSI limit" value={model.plan.dcr_max_FSI}
                    onSave={(v) => updatePlan({ dcr_max_FSI: parseFloat(v) || 0 })} />
              <Row label="DCR ground cov" value={model.plan.dcr_max_ground_coverage}
                    onSave={(v) => updatePlan({ dcr_max_ground_coverage: parseFloat(v) || 0 })} />
              <Row label="Total BUA m²" value={model.plan.total_BUA_m2}
                    onSave={(v) => updatePlan({ total_BUA_m2: parseFloat(v) || 0 })} />
              <Row label="Residential units" value={model.plan.units_residential}
                    onSave={(v) => updatePlan({ units_residential: parseInt(v) || 0 })} />
            </div>
          )}
        </div>
        <div style={{ ...card, padding: 12, overflowY: "auto" }}>
          <div style={label}>DCR Compliance</div>
          {compliance && (
            <div style={{ marginTop: 8, fontSize: 11, lineHeight: 1.7 }}>
              <Stat l="Ground coverage" v={`${(compliance.ground_coverage.value * 100).toFixed(1)}% / ${(compliance.ground_coverage.limit * 100).toFixed(0)}%`}
                    s={compliance.ground_coverage.status} />
              <Stat l="FSI" v={`${compliance.fsi.value.toFixed(2)} / ${compliance.fsi.limit}`}
                    s={compliance.fsi.status} />
              <Stat l="Parking" v={`${compliance.parking.provided} / ${compliance.parking.required}`}
                    s={compliance.parking.status} />
              <div style={{ marginTop: 8 }}>Green: {compliance.green_pct}% · Paved: {compliance.paved_pct}%</div>
              <div>Open-space ratio: {compliance.open_space_ratio}</div>
              <div style={{ marginTop: 8, color: "var(--pf-muted)", fontSize: 10 }}>
                Setbacks: F {compliance.setbacks.front_m}m · R {compliance.setbacks.rear_m}m · S {compliance.setbacks.side_m}m
              </div>
            </div>
          )}
        </div>
      </div>
      {err && <div style={{ position: "absolute", bottom: 8, color: "#FF8888" }}>{err}</div>}
    </div>
  );
}

const Row = ({ label: l, value, onSave }) => {
  const [v, setV] = useState(value);
  useEffect(() => { setV(value); }, [value]);
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <span style={{ color: "var(--pf-muted)" }}>{l}</span>
      <input style={{ ...inputStyle, width: 100, fontSize: 11 }} value={v}
              onChange={(e) => setV(e.target.value)}
              onBlur={() => onSave(v)} />
    </div>
  );
};
const Stat = ({ l, v, s }) => (
  <div style={{ display: "flex", justifyContent: "space-between" }}>
    <span>{l}: <b>{v}</b></span>
    <span style={{ color: s === "PASS" ? "#22c55e" : "#ef4444", fontWeight: 600 }}>{s}</span>
  </div>
);
